import { RefObject } from 'react';
import { ThermalComfortBaseHistory, ThermalComfortHistoryInfo } from '../types';
import { dispose, EChartsOption, init } from 'echarts';
import { DISABLED_DATA_INFO } from '../constants';
import { useTranslation } from 'react-i18next';
import ReactDOMServer from 'react-dom/server';
import { colors } from 'styles/colors';
import { ThermalComfortVariant } from '../components/UnitsInfo/types';
import { colorByThermalComfortVariant, hoverColorByThermalComfortVariant } from '../helpers';
import {
  defaultChartOptions, defaultXAxisConfig, formatterXAxisLabel, X_AXIS_DAYS,
} from '../../constants';
import { createPercentageYAxis, generateDayMonthYearXAxisNames, generateMonthYearXAxisNames } from '../../helpers/graph';
import { ThermalComfortGraphTooltip } from '../components/GraphTooltip';

interface UseGenerateThermalComfortGraphReturn {
  renderThermalComfortChart: (data: ThermalComfortHistoryInfo | undefined) => void;
}

interface TooltipParams {
  data: { value: number, units: number };
  name: string;
  seriesName: ThermalComfortVariant;
  value: number;
}

export function useGenerateThermalComfortGraph(
  containerRef: RefObject<HTMLDivElement>,
  disabled?: boolean,
): UseGenerateThermalComfortGraphReturn {
  const { t } = useTranslation();

  function formatterTooltip(value: TooltipParams, type: 'day' | 'month'): string {
    return ReactDOMServer.renderToString(
      <ThermalComfortGraphTooltip
        date={value.name}
        percentage={value.value}
        units={value.data.units}
        variant={value.seriesName}
        type={type}
      />,
    );
  }

  function createChartOptions(seriesData: EChartsOption['series'], data: ThermalComfortHistoryInfo): EChartsOption {
    const option: EChartsOption = {
      ...defaultChartOptions,
      tooltip: {
        ...defaultChartOptions.tooltip,
        trigger: 'item',
        show: !disabled,
        formatter: (value) => formatterTooltip(value, data.TYPE),
        confine: true,
      },
      xAxis: createXAxis(data),
      yAxis: createPercentageYAxis(),
      series: seriesData,
    };

    return option;
  }

  function createXAxis(data: ThermalComfortHistoryInfo): EChartsOption['xAxis'] {
    const graphData = data.TYPE === 'day' ? generateDayMonthYearXAxisNames(data.HISTORY) : generateMonthYearXAxisNames(data.HISTORY);

    return {
      ...defaultXAxisConfig,
      axisTick: {
        ...defaultXAxisConfig.axisTick,
        interval: data.TYPE === 'day' ? (index) => X_AXIS_DAYS.includes(String(index + 1)) : undefined,
      },
      data: graphData,
      axisLabel: {
        ...defaultXAxisConfig.axisLabel,

        formatter: (value: string) => formatterXAxisLabel(value, data.TYPE, disabled),
      },
    } as EChartsOption['xAxis'];
  }

  function buildChartData(data: ThermalComfortHistoryInfo | undefined): EChartsOption['series'] {
    if (!data) {
      return [
        {
          name: t('semDados'),
          stack: 'total',
          barWidth: '21px',
          type: 'bar',
          cursor: 'not-allowed',
          color: colors.icon['disabled-300'],
          data: DISABLED_DATA_INFO.HISTORY.map((item) => item.PERCENTAGE_COMFORTABLE),
        },
      ];
    }

    const comfortable = data.HISTORY.map((item: ThermalComfortBaseHistory) => ({ units: item.TOTAL_UNITS_COMFORTABLE, value: item.PERCENTAGE_COMFORTABLE }));
    const belowLimit = data.HISTORY.map((item: ThermalComfortBaseHistory) => ({ units: item.TOTAL_UNITS_BELOW_LIMIT, value: item.PERCENTAGE_BELOW_LIMIT }));
    const aboveLimit = data.HISTORY.map((item: ThermalComfortBaseHistory) => ({ units: item.TOTAL_UNITS_ABOVE_LIMIT, value: item.PERCENTAGE_ABOVE_LIMIT }));
    const inoperative = data.HISTORY.map((item: ThermalComfortBaseHistory) => ({ units: item.TOTAL_UNITS_INOPERATIVE, value: item.PERCENTAGE_INOPERATIVE }));

    const seriesData = [
      inoperative,
      aboveLimit,
      belowLimit,
      comfortable,
    ];

    const seriesKeys: ThermalComfortVariant[] = [
      'inoperative',
      'aboveLimit',
      'belowLimit',
      'comfortable',
    ];

    const series = seriesKeys.map((name, index) => ({
      name,
      type: 'bar',
      stack: 'total',
      barWidth: '12px',
      data: seriesData[index],
      color: colorByThermalComfortVariant(name),
      emphasis: {
        focus: 'self',
        blurScope: 'coordinateSystem',
        itemStyle: {
          color: hoverColorByThermalComfortVariant(name),
        },
      },
      blur: {
        itemStyle: {
          color: colorByThermalComfortVariant(name),
          opacity: 1,
        },
      },
    }));

    return series as EChartsOption['series'];
  }

  function renderThermalComfortChart(data: ThermalComfortHistoryInfo | undefined): (() => void) | void {
    if (!containerRef.current) return;

    const seriesData = buildChartData(data);

    dispose(containerRef.current);

    const chartInstance = init(containerRef.current, null, {
      width: 672,
    });

    chartInstance.setOption(createChartOptions(seriesData, data ?? DISABLED_DATA_INFO));

    const resizeHandler = () => {
      chartInstance.resize();
    };

    window.addEventListener('resize', resizeHandler);

    return (): void => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.dispose();
    };
  }

  return {
    renderThermalComfortChart,
  };
}
